import type { NuxtAxiosInstance } from '@/types/axios'
import type {
  FunkiFeedbackCreationPayload,
  FunkiTokenResponse,
  FunkiThreadResponse,
  FunkiMessageResponse,
  FunkiMessagesResponse,
  FunkiRequestOptions,
  FunkiApiError,
  FunkiMessageCreationPayload,
} from '@/types/funki'

const getFunkiApiBaseUrl = () => {
  const config = useRuntimeConfig()
  return config.public.CONFIG_ENV === 'local' ? '/api/assistant' : config.public.ASSISTANT_API_URL
}

// 在測試環境中提供 $fetch 的 fallback
const getFetch = () => {
  if (typeof $fetch !== 'undefined') {
    return $fetch
  }
  // 測試環境的 fallback
  return globalThis.$fetch || (() => Promise.reject(new Error('$fetch not available')))
}

export class FunkiStreamError extends Error {
  constructor(message: string, public override readonly cause?: unknown) {
    super(message)
    this.name = 'FunkiStreamError'
  }
}

export class FunkiApiClient {
  private static instance: FunkiApiClient | null = null
  private baseURL: string | null = null
  private token: string | null = null

  static getInstance(): FunkiApiClient {
    if (!FunkiApiClient.instance) {
      FunkiApiClient.instance = new FunkiApiClient()
    }
    return FunkiApiClient.instance
  }

  setToken(token: string) {
    this.token = token
  }

  getToken(): string | null {
    return this.token
  }

  getCommonHeaders({ lang }: { lang?: string }) {
    const headers: Record<string, string> = {}
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`
    }
    if (lang) {
      headers['Accept-Language'] = lang
    }
    return headers
  }

  getCurrentLang() {
    const nuxtInstance = useNuxtApp()
    const { localeProperties, fallbackLocale, locales } = nuxtInstance.$i18n
    const fallbackLocaleObj = locales?.value?.find(l => l.code === fallbackLocale.value) ?? { name: 'en-US' }
    return localeProperties.value.name ?? fallbackLocaleObj.name ?? 'en-US'
  }

  private isTokenExpiredResponse(response: Response): boolean {
    return response.status === 401
  }

  async request<T>(endpoint: string, options: FunkiRequestOptions = {}): Promise<T> {
    if (!this.token) {
      throw new Error('Token not set. Please call setToken() first.')
    }

    // 延遲初始化 baseURL
    if (!this.baseURL) {
      this.baseURL = getFunkiApiBaseUrl()
    }

    try {
      const fetch = getFetch()
      return await fetch<T>(endpoint, {
        baseURL: this.baseURL,
        headers: {
          ...this.getCommonHeaders({ lang: this.getCurrentLang() }),
          ...options.headers,
        },
        ...options,
      })
    } catch (error: unknown) {
      // 統一處理 401 錯誤
      // stream method mirrors this 401 mapping; refresh-and-retry is handled by store-pinia/funki.ts `handleTokenExpiredError`
      const apiError = error as FunkiApiError
      if (apiError?.status === 401) {
        const tokenExpiredError = new Error(apiError.message || 'Token expired') as FunkiApiError
        Object.assign(tokenExpiredError, { ...apiError, isTokenExpired: true })
        throw tokenExpiredError
      }
      throw error
    }
  }

  async createFunkiMessageStream(threadId: string, payload: FunkiMessageCreationPayload, options: { signal: AbortSignal }): Promise<Response> {
    if (!import.meta.client && process.env.VITEST !== 'true') {
      throw new FunkiStreamError('Streaming is only available on client')
    }
    if (!this.token) {
      throw new FunkiStreamError('Token not set')
    }
    if (!this.baseURL) {
      this.baseURL = getFunkiApiBaseUrl()
    }

    const response = await fetch(`${this.baseURL}/threads/${threadId}/messages/stream`, {
      method: 'POST',
      headers: {
        ...this.getCommonHeaders({ lang: this.getCurrentLang() }),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      signal: options.signal,
    })

    if (!response.ok) {
      const err = new FunkiStreamError(`Stream request failed: ${response.status}`) as FunkiStreamError & { isTokenExpired?: boolean }
      if (this.isTokenExpiredResponse(response)) {
        err.isTokenExpired = true
      }
      throw err
    }

    return response
  }
}

export const funkiApiClient = FunkiApiClient.getInstance()

export async function getFunkiToken(axios: NuxtAxiosInstance): Promise<FunkiTokenResponse> {
  const res = await axios.$post('/assistant/token')

  return res
}

export async function getFunkiThreads(token: string): Promise<FunkiThreadResponse> {
  funkiApiClient.setToken(token)
  return await funkiApiClient.request<FunkiThreadResponse>('/threads/latest')
}

export async function createFunkiThread(token: string): Promise<FunkiThreadResponse> {
  funkiApiClient.setToken(token)
  return await funkiApiClient.request<FunkiThreadResponse>('/threads', {
    method: 'POST',
  })
}

export async function getFunkiMessages(token: string, threadId: string, limit?: number, before?: string) {
  if (!threadId) return

  funkiApiClient.setToken(token)
  return await funkiApiClient.request<FunkiMessagesResponse>(`/threads/${threadId}/messages`, {
    query: {
      limit,
      before,
    },
  })
}

export async function createFunkiMessage(
  token: string,
  threadId: string,
  payload: FunkiMessageCreationPayload
): Promise<FunkiMessageResponse | undefined> {
  if (!threadId || !payload.content) return

  funkiApiClient.setToken(token)
  return await funkiApiClient.request<FunkiMessageResponse>(`/threads/${threadId}/messages`, {
    method: 'POST',
    body: payload,
  })
}

export async function createFeedback(token: string, threadId: string, payload: FunkiFeedbackCreationPayload) {
  if (!threadId || !payload.message_id || !payload.vote) return

  funkiApiClient.setToken(token)
  return await funkiApiClient.request(`/threads/${threadId}/feedback`, {
    method: 'POST',
    body: payload,
  })
}

export async function createFunkiMessageStream(
  token: string,
  threadId: string,
  payload: FunkiMessageCreationPayload,
  options: { signal: AbortSignal }
): Promise<Response> {
  funkiApiClient.setToken(token)
  return await funkiApiClient.createFunkiMessageStream(threadId, payload, options)
}
